import { CameraIcon, X } from "lucide-react";
import { Button } from "primereact/button";
import { Image } from "primereact/image";
import { Toast } from "primereact/toast";
import { useRef, useState } from "react";
import heic2any from "heic2any";
import { showError, showSuccess } from "../../../../util/ShowStatus";
import { formatTimestamp } from "../helper/helperFunctions";

interface PhotoUploadProps {
  label: any;
  name: string;
  images: Record<string, File>;
  isUploadInProgress: boolean;
  setIsUploadInProgress: (value: boolean) => void;
  setImages: (files: Record<string, File>) => void;
  fileValues: any[];
  setFileValues: React.Dispatch<React.SetStateAction<any[]>>;
  timeStampLocationValues: {
    locations: Array<{
      photoKey: string;
      address: string;
      coordinates?: { latitude: number; longitude: number };
    }>;
    timeStamps: Array<{ photoKey: string; timestamp: string }>;
  };
  setTimeStampLocationValues: (values: any) => void;
  classname?: string;
  maxImages?: number;
  photosLoading?: boolean;
  setPhotosLoading?: (value: boolean) => void;
}

const PhotoUpload: React.FC<PhotoUploadProps> = ({
  label,
  name,
  images,
  isUploadInProgress,
  setIsUploadInProgress,
  setImages,
  fileValues,
  setFileValues,
  timeStampLocationValues,
  setTimeStampLocationValues,
  classname,
  maxImages = 10, // Changed default to 10
  photosLoading,
  setPhotosLoading

}) => {
  const toast = useRef<Toast>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isGettingLocation, setIsGettingLocation] = useState<boolean>(false);

  const ALLOWED_TYPES = [
    "image/jpeg",
    "image/png",
    "image/heic",
    "image/heif",
    "image/webp",
  ];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const getPhotoKey = (index?: number) => {
    const baseKey = name.toLowerCase().replace(/\s+/g, "-");
    return index !== undefined ? `${baseKey}-${index}` : baseKey;
  };

  const getCurrentImages = () => {
    const baseKey = getPhotoKey();
    const currentImages: { key: string; file: File }[] = [];

    Object.keys(images).forEach((key) => {
      if (key.startsWith(baseKey)) {
        currentImages.push({ key, file: images[key] });
      }
    });

    return currentImages.sort((a, b) => {
      const aIndex = parseInt(a.key.split("-").pop() || "1");
      const bIndex = parseInt(b.key.split("-").pop() || "1");
      return aIndex - bIndex;
    });
  };

  const getNextIndex = () => {
    const currentImages = getCurrentImages();
    if (currentImages.length === 0) return 1;

    const indices = currentImages.map((img) => {
      const parts = img.key.split("-");
      return parseInt(parts[parts.length - 1]) || 1;
    });

    return Math.max(...indices) + 1;
  };

  const convertHeicToJpeg = async (file: File): Promise<File> => {
    const fileType =
      file.type || `image/${file.name.split(".").pop()?.toLowerCase()}`;
    if (fileType === "image/heic" || fileType === "image/heif") {
      try {
        const conversionResult = (await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 0.8,
        })) as Blob;

        return new File(
          [conversionResult],
          file.name.replace(/\.[^/.]+$/, ".jpg"),
          {
            type: "image/jpeg",
            lastModified: new Date().getTime(),
          }
        );
      } catch (error) {
        console.error("HEIC conversion failed:", error);
        throw new Error("Failed to convert HEIC image");
      }
    }
    return file;
  };

  const validateFile = (file: File): boolean => {
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    const fileType = file.type.toLowerCase();

    // Check file type
    if (!ALLOWED_TYPES.includes(fileType) && fileType !== "") {
      showError(
        `Unsupported file type: ${fileType}. Please upload JPEG, PNG, or HEIC.`,
        toast
      );
      return false;
    } else if (
      fileExtension &&
      !ALLOWED_TYPES.includes(`image/${fileExtension}`) &&
      fileType === ""
    ) {
      showError(
        `Unsupported file type: ${fileExtension}. Please upload JPEG, PNG, or HEIC.`,
        toast
      );
      return false;
    } else if (!fileExtension && fileType === "") {
      showError(
        `Unsupported file type. Please upload JPEG, PNG, or HEIC.`,
        toast
      );
      return false;
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      showError(
        `File "${file.name}" is too large. Maximum size is ${
          MAX_FILE_SIZE / 1024 / 1024
        }MB.`,
        toast
      );
      return false;
    }

    return true;
  };

  const getAddressFromCoordinates = async (
    latitude: number,
    longitude: number
  ): Promise<string> => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
        {
          headers: {
            "Accept-Language": "en",
            "User-Agent": "PhotoUploadApp/1.0",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch address");
      }

      const data = await response.json();
      const address = formatAddress(data.address);
      return address;
    } catch (error) {
      console.error("Error getting address:", error);
      return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
    }
  };

  const formatAddress = (addressData: any): string => {
    const components = [];

    if (addressData.suburb) components.push(addressData.suburb);
    else if (addressData.neighbourhood)
      components.push(addressData.neighbourhood);
    else if (addressData.quarter) components.push(addressData.quarter);

    if (addressData.state_district) components.push(addressData.state_district);
    else if (addressData.town) components.push(addressData.town);
    else if (addressData.village) components.push(addressData.village);

    if (addressData.state) components.push(addressData.state);
    if (addressData.country) components.push(addressData.country);
    if (addressData.postcode) components.push(addressData.postcode);

    return components.join(", ");
  };

  const getCurrentLocation = async (): Promise<{
    address: string;
    coordinates: { latitude: number; longitude: number };
  } | null> => {
    if (!navigator.geolocation) {
      showError("Geolocation is not supported by your browser", toast);
      return null;
    }

    setIsGettingLocation(true);

    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
          });
        }
      );

      const coordinates = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      const address = await getAddressFromCoordinates(
        coordinates.latitude,
        coordinates.longitude
      );

      return {
        address,
        coordinates,
      };
    } catch (error: any) {
      console.error("Geolocation error:", error);
      return null;
    } finally {
      setIsGettingLocation(false);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!event.target.files || event.target.files.length === 0) return;

    const files = Array.from(event.target.files);

    // Check total number of images that would result after upload
    const currentImages = getCurrentImages();
    const remainingSlots = maxImages - currentImages.length;

    if (remainingSlots <= 0) {
      showError(`Maximum of ${maxImages} images allowed`, toast);
      event.target.value = "";
      return;
    }

    // Filter files to only those we can process (valid type and size)
    const validFiles = files.filter((file) => {
      const isValid = validateFile(file);
      if (!isValid) {
        console.log(`Skipping invalid file: ${file.name}`);
      }
      return isValid;
    });

    // Take only the number of files we have slots for
    const filesToProcess = validFiles.slice(0, remainingSlots);

    if (filesToProcess.length === 0) {
      event.target.value = "";
      return;
    }

    // If we're skipping some files, show a message
    if (validFiles.length > remainingSlots) {
      showError(
        `Only ${remainingSlots} images were added (max ${maxImages})`,
        toast
      );
    }

    setIsUploadInProgress(true);

    try {
      const locationData = await getCurrentLocation();
      const now = new Date();
      const timestamp = formatTimestamp(
        new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          now.getHours(),
          now.getMinutes(),
          now.getSeconds(),
          now.getMilliseconds()
        )
      );

      // Process all files
      const newImages: Record<string, File> = {};
      const newFileValues: any[] = [];
      let nextIndex = getNextIndex();

      for (const originalFile of filesToProcess) {
        const photoKey = getPhotoKey(nextIndex);
        nextIndex++;

        try {
          const file = await convertHeicToJpeg(originalFile);

          newImages[photoKey] = file;

          newFileValues.push({
            photoKey,
            file,
            label,
            timestamp,
            location: locationData,
          });

          // Update timestamp and location values
          setTimeStampLocationValues((prev: any) => {
            const filteredLocations = prev.locations.filter(
              (loc: any) => loc.photoKey !== photoKey
            );
            const filteredTimeStamps = prev.timeStamps.filter(
              (ts: any) => ts.photoKey !== photoKey
            );

            if (!locationData) {
              return {
                locations: filteredLocations,
                timeStamps: filteredTimeStamps,
              };
            }
            return {
              locations: [
                ...filteredLocations,
                {
                  photoKey,
                  address: locationData.address,
                  coordinates: locationData.coordinates,
                },
              ],
              timeStamps: [...filteredTimeStamps, { photoKey, timestamp }],
            };
          });
        } catch (error) {
          console.error(`Error processing file ${originalFile.name}:`, error);
          showError(`Failed to process ${originalFile.name}`, toast);
        }
      }

      // Update states with all processed files
      setImages({ ...images, ...newImages });
      setFileValues((prev: any[]) => [...prev, ...newFileValues]);

      showSuccess(
        `Successfully uploaded ${filesToProcess.length} photo(s)`,
        toast
      );
    } catch (error) {
      showError("Failed to process images. Please try again.", toast);
      console.error("Image processing error:", error);
    } finally {
      setIsUploadInProgress(false);
      event.target.value = "";
    }
  };

  const handleRemoveImage = (photoKey: string) => {
    const newImages = { ...images };
    delete newImages[photoKey];
    setImages(newImages);

    setTimeStampLocationValues((prev: any) => ({
      locations: prev.locations.filter((loc: any) => loc.photoKey !== photoKey),
      timeStamps: prev.timeStamps.filter((ts: any) => ts.photoKey !== photoKey),
    }));

    setFileValues((prev: any[]) =>
      prev.filter((item) => item.photoKey !== photoKey)
    );
  };

  const triggerFileInput = () => {
    if (isUploadInProgress) {
      showError("Please wait until the current upload completes.", toast);
      return;
    }

    if (photosLoading) {
      showError("Please wait until all photos are being fetched.", toast);
      return;
    }

    const currentImages = getCurrentImages();
    if (currentImages.length >= maxImages) {
      showError(`Maximum of ${maxImages} images allowed`, toast);
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const currentImages = getCurrentImages();

  // if (isUploadInProgress) {
  //   return (
  //     <div className="tw-absolute tw-inset-0  tw-flex tw-items-center tw-justify-center">
  //       <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-b-2 tw-border-gray-400"></div>
  //     </div>
  //   );
  // }

  return (
    <>
      <Toast ref={toast} />
      <div
        className={`tw-flex tw-flex-col tw-gap-1 tw-w-full tw-flex-1 ${classname}`}
      >
        <label className="leak-label">{label}</label>

        <div
          className={`tw-flex tw-justify-center tw-items-center tw-min-h-[100px] tw-border-solid tw-border-[1px] tw-border-[#D0D5DD] tw-rounded-lg tw-relative`}
        >
          <div
            className="tw-w-full tw-h-full tw-flex tw-flex-col tw-justify-center tw-items-center tw-cursor-pointer tw-p-2"
            onClick={triggerFileInput}
          >
            {currentImages.length === 0 ? (
              isGettingLocation ? (
                <div className="tw-text-sm tw-text-gray-500">
                  Getting location...
                </div>
              ) : (
                <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                  <CameraIcon className="tw-text-xl tw-text-gray-500" />
                  <span className="tw-text-sm tw-text-gray-500">
                    Click to add photos ({currentImages.length}/{maxImages})
                  </span>
                </div>
              )
            ) : (
              <div className="tw-w-full">
                <div className="tw-flex tw-flex-wrap tw-gap-2 tw-w-full tw-mb-2">
                  {isUploadInProgress && (
                    <div className="tw-absolute tw-inset-0 tw-bg-white/40 tw-flex tw-items-center tw-justify-center tw-z-20 tw-pointer-events-none">
                      <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-b-2 tw-border-gray-900"></div>
                    </div>
                  )}
                  {currentImages.map(({ key, file }) => (
                    <div
                      key={key}
                      className="tw-relative tw-w-20 tw-h-20 tw-bg-gray-200 tw-rounded-lg"
                    >
                      <Image
                        src={URL.createObjectURL(file)}
                        alt={`Uploaded ${label}`}
                        className="tw-w-20 tw-h-20 tw-inline-flex tw-object-contain tw-rounded-lg"
                      />
                      {/* // Add this to your image preview */}
                      <div className="tw-absolute tw-bottom-1 tw-left-1 tw-bg-black/50 tw-text-white tw-text-[8px] tw-px-1 tw-py-0.5 tw-rounded">
                        {file.name.split(".").pop()?.toUpperCase()} •{" "}
                        {(file.size / 1024).toFixed(1)} KB
                      </div>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage(key);
                        }}
                        className="tw-absolute tw-top-1 tw-right-1 tw-bg-gray-500 tw-text-white tw-rounded-full tw-p-1 tw-text-xs tw-flex tw-items-center tw-justify-center"
                        disabled={isUploadInProgress}
                      >
                        <X size={12} />
                      </Button>
                    </div>
                  ))}
                </div>
                {currentImages.length < maxImages && (
                  <div className="tw-flex tw-justify-center tw-items-center tw-gap-2">
                    <CameraIcon className="tw-text-lg tw-text-gray-500" />
                    <span className="tw-text-sm tw-text-gray-500">
                      Add more photos ({currentImages.length}/{maxImages})
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          className="tw-hidden"
          onChange={handleFileChange}
          accept="image/*"
          capture="environment"
          multiple // Added to allow multiple file selection
        />
      </div>
    </>
  );
};

export default PhotoUpload;
